import type { DownloadTags } from './download-tags';

// Bounded, nonfragmented ISO BMFF audio files. Sample bytes are never decoded or
// transcoded. Rebuilding a front moov also updates every stco/co64 chunk offset.
type Atom = { type: string; start: number; payload: number; end: number };
const encoder = new TextEncoder();
const concat = (parts: Uint8Array[]) => { const result = new Uint8Array(parts.reduce((n, part) => n + part.length, 0)); let pos = 0; for (const part of parts) { result.set(part, pos); pos += part.length; } return result; };
const u32 = (value: number) => { const bytes = new Uint8Array(4); new DataView(bytes.buffer).setUint32(0, value); return bytes; };
const code = (value: string) => Uint8Array.from(value, char => char.charCodeAt(0));
const box = (type: string, data: Uint8Array) => concat([u32(data.length + 8), code(type), data]);
const full = (type: string, data: Uint8Array) => box(type, concat([u32(0), data]));
const values = (value: unknown): string[] => (Array.isArray(value) ? value : [value]).filter(value => value != null && value !== '').map(value => typeof value === 'object' ? JSON.stringify(value) : String(value));
function atoms(bytes: Uint8Array, begin: number, end: number): Atom[] {
  const result: Atom[] = []; const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let pos = begin; pos < end;) {
    if (pos + 8 > end) throw new Error('M4A 原子头已截断');
    let size = view.getUint32(pos); let header = 8;
    if (size === 1) { if (pos + 16 > end) throw new Error('M4A 扩展长度已截断'); const large = view.getBigUint64(pos + 8); if (large > BigInt(end - pos)) throw new Error('M4A 原子长度无效'); size = Number(large); header = 16; }
    else if (size === 0) size = end - pos;
    if (size < header || pos + size > end) throw new Error('M4A 原子长度无效');
    result.push({ type: String.fromCharCode(...bytes.subarray(pos + 4, pos + 8)), start: pos, payload: pos + header, end: pos + size });
    pos += size;
    if (result.length > 100000) throw new Error('M4A 原子数量超过上限');
  }
  return result;
}
function tagAtoms(tags: DownloadTags): Map<string, Uint8Array> {
  const map = new Map<string, Uint8Array>(); const meta = tags.metadata || {};
  const data = (type: number, bytes: Uint8Array) => box('data', concat([u32(type), u32(0), bytes]));
  const setText = (key: string, value: unknown) => { const list = values(value); if (list.length) map.set(key, box(key, concat(list.map(value => data(1, encoder.encode(value)))))); };
  setText('©nam', tags.title); setText('©ART', tags.artists); setText('©alb', tags.album); setText('aART', meta.albumartist || meta.albumArtist);
  setText('©day', meta.date || meta.year || meta.releaseDate); setText('©gen', meta.genre); setText('©wrt', meta.composer); setText('©lyr', tags.lyrics); setText('cprt', meta.copyright); setText('©cmt', meta.comment);
  for (const [key, no, total] of [['trkn', meta.track?.no || meta.trackNumber, meta.track?.of], ['disk', meta.disk?.no || meta.discNumber, meta.disk?.of]] as const) {
    if (Number.isInteger(Number(no)) && Number(no) > 0 && Number(no) <= 65535) { const value = new Uint8Array(8); const view = new DataView(value.buffer); view.setUint16(2, Number(no)); view.setUint16(4, Math.min(65535, Math.max(0, Number(total) || 0))); map.set(key, box(key, data(0, value))); }
  }
  if (tags.picture) {
    const type = tags.picture.format === 'image/jpeg' ? 13 : tags.picture.format === 'image/png' ? 14 : 0;
    if (!type) throw new Error('M4A 封面只支持 JPEG / PNG');
    map.set('covr', box('covr', data(type, tags.picture.data)));
  }
  map.set('----:MINERADIO_METADATA', box('----', concat([full('mean', encoder.encode('com.apple.iTunes')), full('name', encoder.encode('MINERADIO_METADATA')), data(1, encoder.encode(JSON.stringify(meta)))])));
  return map;
}
export function writeM4aTags(bytes: Uint8Array, tags: DownloadTags): Uint8Array {
  const roots = atoms(bytes, 0, bytes.length); const movies = roots.filter(atom => atom.type === 'moov'); const media = roots.filter(atom => atom.type === 'mdat');
  if (movies.length !== 1 || !media.length || roots.some(atom => ['moof', 'mfra', 'sidx'].includes(atom.type))) throw new Error('此 M4A 使用分段或无效结构，暂不支持安全写标');
  const movie = movies[0]; const newTags = tagAtoms(tags); const containers = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'udta']);
  const raw = (atom: Atom) => bytes.slice(atom.start, atom.end);
  function metadata(atom?: Atom) {
    const children = atom ? atoms(bytes, atom.payload + 4, atom.end) : [];
    if (atom && (atom.end - atom.payload < 4 || bytes[atom.payload] !== 0)) throw new Error('此 M4A 元数据版本暂不支持');
    const oldLists = children.filter(item => item.type === 'ilst');
    if (oldLists.length > 1) throw new Error('M4A 存在重复标签表');
    const old = oldLists[0] ? atoms(bytes, oldLists[0].payload, oldLists[0].end) : [];
    const kept = old.filter(item => {
      if (newTags.has(item.type)) return false;
      if (item.type !== '----') return true;
      const names = atoms(bytes, item.payload, item.end).filter(a => a.type === 'name');
      return !names.some(a => new TextDecoder().decode(bytes.subarray(a.payload + 4, a.end)) === 'MINERADIO_METADATA');
    });
    const ilst = box('ilst', concat([...kept.map(raw), ...newTags.values()]));
    const handler = full('hdlr', concat([u32(0), code('mdir'), new Uint8Array(12), new Uint8Array([0])]));
    return full('meta', concat([...children.filter(item => item.type !== 'ilst').map(raw), ...(children.some(item => item.type === 'hdlr') ? [] : [handler]), ilst]));
  }
  function rewrite(atom: Atom, delta: number, depth = 0): Uint8Array {
    if (depth > 16) throw new Error('M4A 层级超过上限');
    if (['stco', 'co64'].includes(atom.type)) {
      const output = Uint8Array.from(bytes.subarray(atom.payload, atom.end)); const view = new DataView(output.buffer); const width = atom.type === 'stco' ? 4 : 8;
      if (output.length < 8 || output[0] !== 0 || view.getUint32(4) > (output.length - 8) / width) throw new Error('M4A chunk offset 表无效');
      for (let index = 0; index < view.getUint32(4); index++) {
        const pos = 8 + index * width; const original = width === 4 ? BigInt(view.getUint32(pos)) : view.getBigUint64(pos);
        if (!media.some(item => original >= BigInt(item.payload) && original < BigInt(item.end))) throw new Error('M4A 音频偏移超出数据区');
        const shifted = original >= BigInt(movie.end) ? original + BigInt(delta) : original;
        if (shifted < 0 || width === 4 && shifted > 0xffffffffn) throw new Error('M4A 音频偏移超过安全范围');
        if (width === 4) view.setUint32(pos, Number(shifted)); else view.setBigUint64(pos, shifted);
      }
      return box(atom.type, output);
    }
    if (!containers.has(atom.type)) return raw(atom);
    const children = atoms(bytes, atom.payload, atom.end);
    if (atom.type === 'moov') {
      if (children.some(item => item.type === 'mvex')) throw new Error('分段 M4A 暂不支持安全写标');
      if (children.filter(item => item.type === 'udta').length > 1) throw new Error('M4A 存在重复用户数据区');
      const parts = children.map(item => rewrite(item, delta, depth + 1));
      if (!children.some(item => item.type === 'udta')) parts.push(box('udta', metadata()));
      return box(atom.type, concat(parts));
    }
    if (atom.type === 'udta' && depth === 1) {
      const metas = children.filter(item => item.type === 'meta');
      if (metas.length > 1) throw new Error('M4A 存在重复元数据区');
      return box('udta', concat([...children.filter(item => item.type !== 'meta').map(raw), metadata(metas[0])]));
    }
    return box(atom.type, concat(children.map(item => rewrite(item, delta, depth + 1))));
  }
  const provisional = rewrite(movie, 0); const delta = provisional.length - (movie.end - movie.start);
  const final = rewrite(movie, delta);
  return concat(roots.map(item => item === movie ? final : raw(item)));
}
